import React from 'react'
import {
  init,
  swipeBehavior,
  viewport,
  retrieveLaunchParams,
} from '@telegram-apps/sdk-react'
import '@/types/telegram-web-app'
import { setTelegramWebViewDetected } from '@/utils/telegram'

/**
 * Creates a shim for window.Telegram.WebApp if it doesn't exist.
 * This is needed for third-party SDKs (like Boxo) that check for
 * window.Telegram.WebApp to detect Telegram environment.
 *
 * @telegram-apps/sdk-react doesn't set window.Telegram.WebApp,
 * so we create a minimal shim when we detect we're in Telegram.
 */
function ensureTelegramWebAppShim() {
  if (typeof window === 'undefined') return

  // If native Telegram.WebApp already exists, don't override
  if ((window as any).Telegram?.WebApp) {
    console.log('[TgProvider] Native Telegram.WebApp already exists')
    return
  }

  // Create the shim
  if (!(window as any).Telegram) {
    (window as any).Telegram = {}
  }

  (window as any).Telegram.WebApp = {
    // Minimal shim - just enough for detection
    initData: '',
    initDataUnsafe: {},
    version: '6.0',
    platform: 'unknown',
    colorScheme: 'dark',
    themeParams: {},
    isExpanded: true,
    viewportHeight: window.innerHeight,
    viewportStableHeight: window.innerHeight,
    // Mark this as a shim so we can identify it if needed
    __isShim: true,
  }

  console.log('[TgProvider] Created Telegram.WebApp shim for third-party SDK detection')
}

const initMiniApp = async () => {
  console.log('initMiniApp start')
  try {
    console.log('[TgProvider] init starting...')
    init()

    // Mark as Telegram WebView since init() succeeded
    setTelegramWebViewDetected(true)

    // Create shim for third-party SDKs that check window.Telegram.WebApp
    ensureTelegramWebAppShim()

    await viewport.mount()
    console.log('[TgProvider] viewport mounted successfully!')

    // Log offsets right after mounting
    const safeArea = viewport.safeAreaInsets()
    const contentSafeArea = viewport.contentSafeAreaInsets()
    console.log('[TgProvider] safeAreaInsets after mount:', safeArea)
    console.log(
      '[TgProvider] contentSafeAreaInsets after mount:',
      contentSafeArea,
    )

    swipeBehavior.mount()
    swipeBehavior.disableVertical()
    console.log('[TgProvider] swipeBehavior configured')

    const launchParams = await retrieveLaunchParams()
    console.log('[TgProvider] launchParams:', launchParams)

    const disableFullscreen = [
      'macos',
      'tdesktop',
      'weba',
      'web',
      'webk',
    ].includes(launchParams.tgWebAppPlatform)

    console.log(
      '[TgProvider] Platform:',
      launchParams.tgWebAppPlatform,
      'disableFullscreen:',
      disableFullscreen,
    )

    const isFullscreenAvailable = await viewport.requestFullscreen.isAvailable()
    console.log('[TgProvider] isFullscreenAvailable:', isFullscreenAvailable)

    if (isFullscreenAvailable && !disableFullscreen) {
      viewport.requestFullscreen()
    } else {
      if (viewport.expand.isAvailable()) {
        viewport.expand()
      }
    }
  } catch (error) {
    // Telegram SDK initialization error - silently continue
    console.log(error)
  }
}

// Run initialization at module level (always)
initMiniApp()

const TgProvider = ({ children }: { children: React.ReactNode }) => {
  return <>{children}</>
}

export default TgProvider
